import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Toast } from "antd-mobile";
import { FormInstance } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    ChevronDown,
    Loader2,
    ArrowLeft,
    User,
    Mail,
    Phone,
    Briefcase,
    MapPin,
    Calendar,
    Award,
    Shield,
    FileText,
    Info,
    GraduationCap,
    Heart,
    Handshake,
    Store,
    Activity,
    Users,
    Check
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import { genderOptions } from "../utils/formOptions";
import { cn } from "../lib/utils";
import GoogleAddressInput from "../components/GoogleAddressInput";
import { Location } from "../utils/types/tenantUser";
import {
    DropdownService,
    DynamicModuleService,
    ZulipService,
    RosterService,
} from "../services/APIs";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";
import OrganizationProfileDetailsService from "../services/APIs/OrganizationProfileDetails";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import BackButton from "../components/BackButton";
import { Button } from "../components/ui/button";

// Constants for theme colors since CITYCOLLAB_COLORS might be missing
const CITYCOLLAB_COLORS = {
    primary: "hsl(var(--primary))",
    textBase: "hsl(var(--foreground))",
};

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5 }
    }
};

// Section Header Component
const SectionHeader = ({ icon: Icon, title, description }: { icon: any; title: string, description?: string }) => (
    <motion.div
        variants={itemVariants}
        className="mb-8"
    >
        <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10 text-primary shrink-0">
                <Icon size={20} />
            </div>
            <h3 className="text-xl font-bold text-slate-800 font-display tracking-tight leading-tight">{title}</h3>
        </div>
        {description && <p className="text-sm text-muted-foreground ml-4 sm:ml-11 mt-1">{description}</p>}
    </motion.div>
);

const FormLabel = ({
    label,
    required,
    showAsterisk = true,
}: {
    label: string;
    required?: boolean;
    showAsterisk?: boolean;
}) => (
    <Label className="text-sm font-semibold text-slate-700 mb-2 block">
        {label}
        {required && showAsterisk && <span className="text-destructive ml-1">*</span>}
    </Label>
);

// Wrapper for inputs that are nested inside divs for icons
const FormInput = ({ icon: Icon, className, ...props }: any) => (
    <div className="relative">
        {Icon && <Icon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />}
        <Input {...props} className={cn(Icon ? "pl-11" : "", "h-12 rounded-xl", className)} />
    </div>
);

const FormTextArea = ({ icon: Icon, className, ...props }: any) => (
    <div className="relative">
        {Icon && <Icon className="absolute left-4 top-4 h-4 w-4 text-muted-foreground z-10" />}
        <textarea
            {...props}
            className={cn(
                "w-full min-h-[120px] rounded-2xl border border-input bg-background pr-4 py-3 text-sm text-slate-700 transition-all hover:border-primary/50 focus:border-primary outline-none resize-none",
                Icon ? "pl-11" : "px-4",
                className
            )}
        />
    </div>
);

// Fallback Loader Component
const SmoothLoaderComponent = ({ message }: { message: string; variant?: string }) => (
    <div className="flex h-screen flex-col items-center justify-center space-y-4 bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-base font-medium text-slate-600"
        >
            {message}
        </motion.p>
    </div>
);

// Fallback for missing Image Upload component
const CustomImagePlaceholder = ({ value, onChange }: { value: any; onChange: (val: any) => void }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [preview, setPreview] = useState<string | null>(null);

    useEffect(() => {
        if (typeof value === "string" && value.startsWith("http")) {
            setPreview(value);
        }
    }, [value]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result as string);
                onChange(file); // This might need to be an upload call in a real app
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <div
            className="flex h-32 w-32 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-primary"
            onClick={() => fileInputRef.current?.click()}
        >
            {preview ? (
                <img src={preview} alt="Profile" className="h-full w-full rounded-lg object-cover" />
            ) : (
                <span className="text-xs text-muted-foreground">Upload photo</span>
            )}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                accept="image/*"
            />
        </div>
    );
};

const MemberRegistrationPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const referCodeParam = searchParams.get("refer_code");
    const [form] = Form.useForm();
    const basicInfoSchema = "MM Member";

    // Custom dropdown states
    const [selectedPrefix, setSelectedPrefix] = useState<string>("");
    const [selectedSuffix, setSelectedSuffix] = useState<string>("");
    const [selectedGender, setSelectedGender] = useState<string>("");
    const [selectedDepartment, setSelectedDepartment] = useState<string>("");
    const [selectedPosition, setSelectedPosition] = useState<string>("");
    const [showPrefixOptions, setShowPrefixOptions] = useState(false);
    const [showSuffixOptions, setShowSuffixOptions] = useState(false);
    const [showGenderOptions, setShowGenderOptions] = useState(false);
    const [showDepartmentOptions, setShowDepartmentOptions] = useState(false);
    const [showSkillsOptions, setShowSkillsOptions] = useState(false);
    const [showPositionOptions, setShowPositionOptions] = useState(false);
    const [_addressDetails, setAddressDetails] = useState<Location | null>(null);

    // File upload states
    const [profilePhoto, setProfilePhoto] = useState<any>(null);
    const [isLoader, setIsLoader] = useState(false);
    const [memberId, setMemberId] = useState<string | null>(null);
    const formRef = useRef<FormInstance>(null);
    const isUpdatingProfileImg = useRef(false);

    // Multi-select state
    const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
    const [selectedSkillInterests, setSelectedSkillInterests] = useState<string[]>([]);
    const [selectedSeniority, setSelectedSeniority] = useState<string>("");
    const [showSkillInterestsOptions, setShowSkillInterestsOptions] = useState(false);
    const [showSeniorityOptions, setShowSeniorityOptions] = useState(false);
    const [_cityAddressDetails, setCityAddressDetails] = useState<Location | null>(null);

    // New dropdown states
    const [selectedConsultation, setSelectedConsultation] = useState<string>("");
    const [selectedContract, setSelectedContract] = useState<string>("");
    const [selectedMarketplace, setSelectedMarketplace] = useState<string>("");
    const [selectedOpportunities, setSelectedOpportunities] = useState<string>("");
    const [showConsultationOptions, setShowConsultationOptions] = useState(false);
    const [showContractOptions, setShowContractOptions] = useState(false);
    const [showMarketplaceOptions, setShowMarketplaceOptions] = useState(false);
    const [showOpportunitiesOptions, setShowOpportunitiesOptions] = useState(false);

    // Helper functions for multi-select
    const toggleSkill = (skillValue: string) => {
        const next = selectedSkills.includes(skillValue)
            ? selectedSkills.filter(s => s !== skillValue)
            : [...selectedSkills, skillValue];
        setSelectedSkills(next);
        form.setFieldValue("skill", next);
    };

    const toggleInterest = (interestValue: string) => {
        const next = selectedSkillInterests.includes(interestValue)
            ? selectedSkillInterests.filter(i => i !== interestValue)
            : [...selectedSkillInterests, interestValue];
        setSelectedSkillInterests(next);
        form.setFieldValue("skillintrests", next);
    };

    const OrganizationId = localStorage.getItem("OrganizationId");
    const departmentId = localStorage.getItem("DepartmentId");
    const departmentName = localStorage.getItem("DepartmentName");
    const isDepartmentReadonly = !!departmentName;

    // Simple loading state
    const [isCheckingMemberLoading, setIsCheckingMemberLoading] = useState(true);
    const [isCheckingMemberLoadingShowContent, setIsCheckingMemberLoadingShowContent] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsCheckingMemberLoading(false);
            setIsCheckingMemberLoadingShowContent(true);
        }, 800);
        return () => clearTimeout(timer);
    }, []);

    // Prefill signup/login email on mount
    useEffect(() => {
        const signupEmail = localStorage.getItem("signupEmail");
        const userEmail = localStorage.getItem("userEmail");
        const userInfoStr = localStorage.getItem("userInfo");
        let email = signupEmail || userEmail;

        if (!email && userInfoStr) {
            try {
                const userInfo = JSON.parse(userInfoStr);
                email = userInfo.email ?? userInfo.user_email ?? userInfo.Email ?? "";
            } catch (e) {
                console.error("Error parsing userInfo:", e);
            }
        }

        if (email) {
            // Use setTimeout to ensure form instance is fully ready
            const timer = setTimeout(() => {
                form.setFieldValue("email", email);
                const savedOrgId = localStorage.getItem("OrganizationId");
                if (savedOrgId) {
                    form.setFieldValue("organizationid", savedOrgId);
                }
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [form]);

    // Check member by email and populate form fields
    useEffect(() => {
        const checkMemberByEmail = async () => {
            try {
                const userInfo = localStorage.getItem("userInfo");
                let email = "";

                if (userInfo) {
                    try {
                        const user = JSON.parse(userInfo);
                        email = user.email ?? user.user_email ?? "";
                    } catch (e) {
                        console.error("Error parsing userInfo:", e);
                    }
                }

                const signupEmail = localStorage.getItem("signupEmail");
                if (signupEmail) {
                    email = signupEmail;
                } else if (!email) {
                    email = localStorage.getItem("userEmail") ?? "";
                }

                // Final fallback if still empty
                if (!email && userInfo) {
                    try {
                        const user = JSON.parse(userInfo);
                        email = user.email ?? user.user_email ?? "";
                    } catch (e) { }
                }

                if (email) {
                    const response = await RosterService.checkMemberByEmail(email, referCodeParam || undefined);

                    if (response?.data) {
                        const memberData = response.data;
                        if (memberData.id) {
                            setMemberId(memberData.id);
                        }

                        setTimeout(() => {
                            const fieldsToSet: any = {};
                            if (memberData.first_name) fieldsToSet.first_name = memberData.first_name;
                            if (memberData.middle_name) fieldsToSet.middle_name = memberData.middle_name;
                            if (memberData.last_name) fieldsToSet.last_name = memberData.last_name;

                            if (Object.keys(fieldsToSet).length > 0) {
                                form.setFieldsValue(fieldsToSet);
                            }
                        }, 100);

                        if (memberData.prefix) {
                            form.setFieldValue("prefix", memberData.prefix);
                            setSelectedPrefix(memberData.prefix);
                        }
                        if (memberData.member_suffix) {
                            form.setFieldValue("member_suffix", memberData.member_suffix);
                            setSelectedSuffix(memberData.member_suffix);
                        }
                        if (memberData.gender) {
                            form.setFieldValue("gender", memberData.gender);
                            setSelectedGender(memberData.gender);
                        }
                        if (memberData.phonenumber) form.setFieldValue("phonenumber", memberData.phonenumber);
                        if (memberData.dob) form.setFieldValue("dob", memberData.dob);
                        if (memberData.workemail) form.setFieldValue("workemail", memberData.workemail);

                        if (memberData.skillinterests) {
                            const skills = typeof memberData.skillinterests === "string"
                                ? memberData.skillinterests.split("::").filter(Boolean)
                                : Array.isArray(memberData.skillinterests)
                                    ? memberData.skillinterests
                                    : [];
                            if (skills.length > 0) {
                                setSelectedSkillInterests(skills);
                                form.setFieldValue("skillintrests", skills);
                            }
                        }
                        if (memberData.seniority) {
                            form.setFieldValue("seniority", memberData.seniority);
                            setSelectedSeniority(memberData.seniority);
                        }
                        if (memberData.profileimg) {
                            form.setFieldValue("profileimg", memberData.profileimg);
                            setProfilePhoto(memberData.profileimg);
                        }
                        if (memberData.email) {
                            form.setFieldValue("email", memberData.email);
                        } else {
                            // If member data has no email, ensure we keep the prefilled one
                            const signupEmail = localStorage.getItem("signupEmail");
                            const userEmail = localStorage.getItem("userEmail");
                            const userInfoStr = localStorage.getItem("userInfo");
                            let fallbackEmail = signupEmail || userEmail;

                            if (!fallbackEmail && userInfoStr) {
                                try {
                                    const userInfo = JSON.parse(userInfoStr);
                                    fallbackEmail = userInfo.email ?? userInfo.user_email ?? "";
                                } catch (e) { }
                            }
                            if (fallbackEmail) form.setFieldValue("email", fallbackEmail);
                        }

                        if (memberData.organizationid) {
                            form.setFieldValue("organizationid", memberData.organizationid);
                            localStorage.setItem("OrganizationId", memberData.organizationid);
                        }

                        if (memberData.consultation) {
                            const val = String(memberData.consultation);
                            form.setFieldValue("consultation", val);
                            setSelectedConsultation(val);
                        }
                        if (memberData.contract) {
                            const val = String(memberData.contract);
                            form.setFieldValue("contract", val);
                            setSelectedContract(val);
                        }
                        if (memberData.marketplace) {
                            const val = String(memberData.marketplace);
                            form.setFieldValue("marketplace", val);
                            setSelectedMarketplace(val);
                        }
                        if (memberData.opportunities) {
                            const val = String(memberData.opportunities);
                            form.setFieldValue("opportunities", val);
                            setSelectedOpportunities(val);
                        }
                    }
                }
            } catch (error) {
                console.error("Error checking member by email:", error);
            }
        };

        if (form) {
            checkMemberByEmail();
        }
    }, [OrganizationId, form]);

    useEffect(() => {
        const handleClickOutside = () => {
            setShowPrefixOptions(false);
            setShowSuffixOptions(false);
            setShowGenderOptions(false);
            setShowSkillsOptions(false);
            setShowPositionOptions(false);
            setShowDepartmentOptions(false);
            setShowSkillInterestsOptions(false);
            setShowSeniorityOptions(false);
            setShowConsultationOptions(false);
            setShowContractOptions(false);
            setShowMarketplaceOptions(false);
            setShowOpportunitiesOptions(false);
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const removeEmpty = (obj: Record<string, any>) => {
        const cleaned: Record<string, any> = {};
        for (const key in obj) {
            const val = obj[key];
            if (val !== null && val !== undefined && !(Array.isArray(val) && val.length === 0) && !(typeof val === "string" && val.trim() === "")) {
                cleaned[key] = val;
            }
        }
        return cleaned;
    };

    const { data: prefixOptions } = useQuery({
        queryKey: ["prefix_child"],
        queryFn: () => DropdownService.getDropdownData("prefix_child", false),
    });

    const { data: departmentOptions } = useQuery({
        queryKey: ["department"],
        queryFn: () => DropdownService.getDropdownData("department", false),
    });

    const { data: positionOptions } = useQuery({
        queryKey: ["position"],
        queryFn: () => DropdownService.getDropdownData("position", false),
    });

    const { data: suffixOptions } = useQuery({
        queryKey: ["suffix"],
        queryFn: () => DropdownService.getDropdownData("suffix", false),
    });

    const { data: skillsOptions } = useQuery({
        queryKey: ["skill_interest"],
        queryFn: () => DropdownService.getDropdownData("skill_interest", false),
    });

    const handleBackToLogin = () => {
        // Clear tokens and registration-specific IDs to ensure we see the Sign In form
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("memberId");
        navigate(CitycollabNavigationRoutes.LoginPage);
    };

    const onFinish = async (values: any) => {
        const dobFormatted = values.dob ? dayjs(values.dob).format("YYYY-MM-DD") : null;
        const userId = localStorage.getItem("userId");
        const tenantId = localStorage.getItem("tenant");
        const profileImgValue = values.profileimg ?? profilePhoto;

        const payload: any = {
            verification: "Pending",
            member_status: "false",
            tenant: tenantId ?? null,
            jdid: null,
            first_name: values.first_name ?? null,
            middle_name: values.middle_name ?? null,
            last_name: values.last_name ?? null,
            member_suffix: values.member_suffix ?? null,
            profileimg: profileImgValue ?? null,
            phonenumber: values.phonenumber ?? null,
            gender: values.gender ?? null,
            dob: dobFormatted ?? null,
            address: values.address ?? null,
            profession: null,
            employer: null,
            member_industry: null,
            organization: null,
            governmentid: null,
            governmentidnumber: null,
            govimage: null,
            description: values.description ?? null,
            username: userId ?? null,
            certificate1: values.certificate1 ?? null,
            certificate2: null,
            prefix: values.prefix ?? null,
            department: values.department ?? null,
            position: values.position ?? null,
            profilephoto: null,
            organizationid: values.organizationid ?? OrganizationId ?? localStorage.getItem("OrganizationId") ?? null,
            cityaddress: values.cityaddress ?? null,
            workemail: values.workemail ?? null,
            workphone: null,
            supervisor: values.supervisor ?? null,
            seniority: values.seniority ?? null,
            responsibility: values.responsibility ?? null,
            emergencyroles: values.emergencyroles ?? null,
            skillinterests: values.skillintrests ?? selectedSkillInterests ?? [],
            whatsapp: values.whatsapp ?? null,
            twitter: values.twitter ?? null,
            facebook: values.facebook ?? null,
            linkedin: values.linkedin ?? null,
            consultation: values.consultation ?? null,
            contract: values.contract ?? null,
            marketplace: values.marketplace ?? null,
            rdate: null,
            opportunities: values.opportunities ?? null,
            awards: null,
            certificate: null,
            approve: null,
            email: values.email ?? null,
            retire: null,
            skill: values.skill ?? selectedSkills ?? [],
        };

        setIsLoader(true);
        try {
            let response;
            if (memberId) {
                response = await DynamicModuleService.updateItem(basicInfoSchema ?? "", memberId, payload);
            } else {
                const formattedData = removeEmpty({ ...payload, skillinterests: payload.skillinterests });
                response = await DynamicModuleService.createItem(basicInfoSchema ?? "", formattedData);
            }

            if (response) {
                const resId = response.id ?? memberId;
                if (resId) localStorage.setItem("memberId", resId);

                try {
                    const existingToken = localStorage.getItem("zulipToken");
                    if (!existingToken) {
                        const zulipRes = await ZulipService.getZulipToken();
                        localStorage.setItem("zulipToken", zulipRes.basic_token);
                        localStorage.setItem("zulipUserId", zulipRes.zulip_user_id);
                    }
                } catch (err) {
                    console.error("Zulip error:", err);
                }

                localStorage.removeItem("signupEmail");
                navigate(CitycollabNavigationRoutes.NEW_DESIGN);
            }
        } catch (err) {
            console.error("Registration failed:", err);
        } finally {
            setIsLoader(false);
        }
    };

    const handleAddressChange = (details?: Location) => {
        if (details) {
            form.setFieldsValue({ address: details.formatted_address });
            setAddressDetails(details);
            form.setFields([{ name: "address", errors: [] }]);
        }
    };

    const handleCityAddressChange = (details?: Location) => {
        if (details) {
            form.setFieldsValue({ cityaddress: details.formatted_address });
            setCityAddressDetails(details);
            form.setFields([{ name: "cityaddress", errors: [] }]);
        }
    };

    const skillInterestsOptions = [
        { label: "Computer", value: "computer" },
        { label: "Math", value: "math" },
        { label: "Science", value: "science" },
        { label: "Arts", value: "arts" },
        { label: "Music", value: "music" },
        { label: "Sports", value: "sports" },
        { label: "Leadership", value: "leadership" },
        { label: "Communication", value: "Communication" },
    ];

    const seniorityOptions = [
        { label: "Director", value: "director" },
        { label: "Manager", value: "manager" },
        { label: "Supervisor", value: "supervisor" },
        { label: "Staff", value: "staff" },
        { label: "Intern", value: "intern" },
    ];

    const handleProfilePhotoChange = useCallback((value: any) => {
        setProfilePhoto(value);
        isUpdatingProfileImg.current = true;
        form.setFieldsValue({ profileimg: value });
        setTimeout(() => { isUpdatingProfileImg.current = false; }, 100);
    }, [form]);

    const handleFormValuesChange = useCallback((changedFields: any) => {
        if (isUpdatingProfileImg.current) return;
        const fieldNames = Object.keys(changedFields);
        if (fieldNames.length === 1 && fieldNames[0] === "profileimg") return;
        const fieldsToUpdate = fieldNames.filter(name => name !== "profileimg");
        if (fieldsToUpdate.length > 0) {
            form.setFields(fieldsToUpdate.map(fieldName => ({ name: fieldName, errors: [] })));
        }
    }, [form]);

    if (isCheckingMemberLoading) {
        return <SmoothLoaderComponent message="Checking member data..." />;
    }

    return (
        <div className="min-h-screen bg-slate-50/50 py-8 md:py-12 px-4 md:px-6">
            <motion.div
                initial="hidden"
                animate={isCheckingMemberLoadingShowContent ? "visible" : "hidden"}
                variants={containerVariants}
                className="max-w-6xl mx-auto"
            >
                {/* Header Section */}
                <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <motion.div variants={itemVariants}>
                        <div className="flex items-center gap-4 mb-3">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleBackToLogin}
                                className="h-10 w-10 rounded-xl bg-white shadow-sm border border-slate-200 hover:bg-slate-50 transition-all"
                            >
                                <ArrowLeft className="h-5 w-5 text-slate-600" />
                            </Button>
                            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 font-display tracking-tight leading-none">
                                Member Registration
                            </h1>
                        </div>
                        <p className="text-muted-foreground text-base sm:text-lg font-medium ml-4 sm:ml-14">
                            Complete your professional member profile
                        </p>
                    </motion.div>

                </div>

                <motion.div
                    variants={itemVariants}
                    className="bg-white rounded-3xl md:rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-200 overflow-hidden"
                >
                    <div className="p-4 sm:p-8 md:p-12">
                        <Form
                            ref={formRef}
                            form={form}
                            onFinish={onFinish}
                            layout="vertical"
                            onValuesChange={handleFormValuesChange}
                            style={{ "--border-top": "none", "--border-bottom": "none", "--border-inner": "none" }}
                        >
                            {/* Hidden field for organizationid to ensure it's in the values object */}
                            <Form.Item name="organizationid" hidden>
                                <Input />
                            </Form.Item>

                            {/* Personal Profile Section */}
                            <SectionHeader
                                icon={User}
                                title="Personal Profile"
                                description="Basic identification and personal information"
                            />

                            <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-6 mb-12">
                                <div className="order-2 lg:order-1 lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    <Form.Item name="prefix" label={<FormLabel label="Prefix" />}>
                                        <div className="relative" onClick={e => e.stopPropagation()}>
                                            <div
                                                onClick={e => { e.stopPropagation(); setShowPrefixOptions(!showPrefixOptions); }}
                                                className="flex h-12 w-full cursor-pointer items-center justify-between rounded-xl border border-input bg-background px-4 py-2 text-sm transition-all hover:border-primary/50"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <Info className="h-4 w-4 text-muted-foreground" />
                                                    <span className="font-medium text-slate-700">{prefixOptions?.find((opt: any) => opt.value === selectedPrefix)?.label ?? "Prefix"}</span>
                                                </div>
                                                <ChevronDown size={16} className="text-muted-foreground" />
                                            </div>
                                            <AnimatePresence>
                                                {showPrefixOptions && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: -10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: -10 }}
                                                        className="absolute z-50 mt-2 max-h-60 w-full overflow-y-auto rounded-xl border bg-white shadow-2xl border-slate-100 p-1"
                                                    >
                                                        {prefixOptions?.map((option: any) => (
                                                            <div
                                                                key={option.value}
                                                                className="relative flex w-full cursor-pointer select-none items-center rounded-lg py-2.5 pl-9 pr-3 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-primary transition-colors"
                                                                onClick={() => {
                                                                    setSelectedPrefix(option.value);
                                                                    form.setFieldValue("prefix", option.value);
                                                                    setShowPrefixOptions(false);
                                                                }}
                                                            >
                                                                {selectedPrefix === option.value && <Check className="absolute left-3 h-4 w-4 text-primary" />}
                                                                {option.label}
                                                            </div>
                                                        ))}
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </Form.Item>

                                    <Form.Item name="first_name" label={<FormLabel label="First Name" required showAsterisk={false} />} rules={[{ required: true, message: "Required" }]}>
                                        <FormInput icon={User} placeholder="First name" />
                                    </Form.Item>

                                    <Form.Item name="middle_name" label={<FormLabel label="Middle Name" />}>
                                        <FormInput icon={User} placeholder="Middle name" />
                                    </Form.Item>

                                    <Form.Item name="last_name" label={<FormLabel label="Last Name" />}>
                                        <FormInput icon={User} placeholder="Last name" />
                                    </Form.Item>

                                    <Form.Item name="member_suffix" label={<FormLabel label="Suffix" />}>
                                        <div className="relative" onClick={e => e.stopPropagation()}>
                                            <div
                                                onClick={e => { e.stopPropagation(); setShowSuffixOptions(!showSuffixOptions); }}
                                                className="flex h-12 w-full cursor-pointer items-center justify-between rounded-xl border border-input bg-background px-4 py-2 text-sm transition-all hover:border-primary/50"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div className="h-4 w-4" />
                                                    <span className="font-medium text-slate-700">{suffixOptions?.find((opt: any) => opt.value === selectedSuffix)?.label ?? "Suffix"}</span>
                                                </div>
                                                <ChevronDown size={16} className="text-muted-foreground" />
                                            </div>
                                            <AnimatePresence>
                                                {showSuffixOptions && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: -10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: -10 }}
                                                        className="absolute z-50 mt-2 max-h-60 w-full overflow-y-auto rounded-xl border bg-white shadow-2xl border-slate-100 p-1"
                                                    >
                                                        {suffixOptions?.map((option: any) => (
                                                            <div
                                                                key={option.value}
                                                                className="relative flex w-full cursor-pointer select-none items-center rounded-lg py-2.5 pl-9 pr-3 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-primary transition-colors"
                                                                onClick={() => {
                                                                    setSelectedSuffix(option.value);
                                                                    form.setFieldValue("member_suffix", option.value);
                                                                    setShowSuffixOptions(false);
                                                                }}
                                                            >
                                                                {selectedSuffix === option.value && <Check className="absolute left-3 h-4 w-4 text-primary" />}
                                                                {option.label}
                                                            </div>
                                                        ))}
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </Form.Item>

                                    <Form.Item
                                        name="gender"
                                        label={<FormLabel label="Gender" required showAsterisk={false} />}
                                        rules={[{ required: true, message: "Required" }]}
                                    >
                                        <div className="relative" onClick={e => e.stopPropagation()}>
                                            <div
                                                onClick={e => { e.stopPropagation(); setShowGenderOptions(!showGenderOptions); }}
                                                className="flex h-12 w-full cursor-pointer items-center justify-between rounded-xl border border-input bg-background px-4 py-2 text-sm transition-all hover:border-primary/50"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <Users className="h-4 w-4 text-muted-foreground" />
                                                    <span className="font-medium text-slate-700">{selectedGender || "Select Gender"}</span>
                                                </div>
                                                <ChevronDown size={16} className="text-muted-foreground" />
                                            </div>
                                            <AnimatePresence>
                                                {showGenderOptions && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: -10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: -10 }}
                                                        className="absolute z-50 mt-2 max-h-60 w-full overflow-y-auto rounded-xl border bg-white shadow-2xl border-slate-100 p-1"
                                                    >
                                                        {genderOptions.map(option => (
                                                            <div
                                                                key={option}
                                                                className="relative flex w-full cursor-pointer select-none items-center rounded-lg py-2.5 pl-9 pr-3 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-primary transition-colors"
                                                                onClick={() => {
                                                                    setSelectedGender(option);
                                                                    form.setFieldValue("gender", option);
                                                                    setShowGenderOptions(false);
                                                                }}
                                                            >
                                                                {selectedGender === option && <Check className="absolute left-3 h-4 w-4 text-primary" />}
                                                                {option}
                                                            </div>
                                                        ))}
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </Form.Item>

                                    <Form.Item name="dob" label={<FormLabel label="Date of Birth" required showAsterisk={false} />} rules={[{ required: true, message: "Required" }]}>
                                        <FormInput icon={Calendar} type="date" placeholder="dd-mm-yyyy" />
                                    </Form.Item>
                                </div>

                                <div className="order-1 lg:order-2 lg:col-span-3 flex flex-col items-center justify-center lg:border-l lg:border-slate-100 lg:pl-8">
                                    <Form.Item name="profileimg" label={<FormLabel label="Photo" />}>
                                        <CustomImagePlaceholder
                                            value={profilePhoto}
                                            onChange={handleProfilePhotoChange}
                                        />
                                    </Form.Item>
                                </div>
                            </motion.div>

                            {/* Professional Details Section */}
                            <SectionHeader
                                icon={Briefcase}
                                title="Professional Details"
                                description="Your role and expertise within the organization"
                            />

                            <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-6 mb-12">
                                <Form.Item
                                    name="department"
                                    className="md:col-span-12 lg:col-span-7"
                                    label={<FormLabel label="Department" required showAsterisk={false} />}
                                    rules={[{ required: true, message: "Required" }]}
                                >
                                    <div className="relative" onClick={e => e.stopPropagation()}>
                                        <div
                                            onClick={e => { if (!isDepartmentReadonly) { e.stopPropagation(); setShowDepartmentOptions(!showDepartmentOptions); } }}
                                            className={`flex h-12 w-full items-center justify-between rounded-xl border border-input bg-background px-4 py-2 text-sm transition-all ${isDepartmentReadonly ? "cursor-not-allowed opacity-60 bg-slate-50" : "cursor-pointer hover:border-primary/50"}`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Briefcase className="h-4 w-4 text-muted-foreground" />
                                                <span className="font-medium text-slate-700">{departmentOptions?.find((opt: any) => opt.value === selectedDepartment)?.label ?? "Select Department"}</span>
                                            </div>
                                            {!isDepartmentReadonly && <ChevronDown size={16} className="text-muted-foreground" />}
                                        </div>
                                        <AnimatePresence>
                                            {showDepartmentOptions && (
                                                <motion.div
                                                    initial={{ opacity: 0, scale: 0.95 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    exit={{ opacity: 0, scale: 0.95 }}
                                                    className="absolute z-50 mt-2 max-h-60 w-full overflow-y-auto rounded-xl border bg-white shadow-2xl border-slate-100 p-1"
                                                >
                                                    {departmentOptions?.map((option: any) => (
                                                        <div
                                                            key={option.value}
                                                            className="relative flex w-full cursor-pointer select-none items-center rounded-lg py-2.5 pl-9 pr-3 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-primary transition-colors"
                                                            onClick={() => {
                                                                setSelectedDepartment(option.value);
                                                                form.setFieldValue("department", option.value);
                                                                setShowDepartmentOptions(false);
                                                            }}
                                                        >
                                                            {selectedDepartment === option.value && <Check className="absolute left-3 h-4 w-4 text-primary" />}
                                                            {option.label}
                                                        </div>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </Form.Item>

                                <Form.Item
                                    name="position"
                                    className="md:col-span-12 lg:col-span-5"
                                    label={<FormLabel label="Position" required showAsterisk={false} />}
                                    rules={[{ required: true, message: "Required" }]}
                                >
                                    <div className="relative" onClick={e => e.stopPropagation()}>
                                        <div
                                            onClick={e => { e.stopPropagation(); setShowPositionOptions(!showPositionOptions); }}
                                            className="flex h-12 w-full cursor-pointer items-center justify-between rounded-xl border border-input bg-background px-4 py-2 text-sm transition-all hover:border-primary/50"
                                        >
                                            <div className="flex items-center gap-2">
                                                <GraduationCap className="h-4 w-4 text-muted-foreground" />
                                                <span className="font-medium text-slate-700">{positionOptions?.find((opt: any) => opt.value === selectedPosition)?.label ?? "Select Position"}</span>
                                            </div>
                                            <ChevronDown size={16} className="text-muted-foreground" />
                                        </div>
                                        <AnimatePresence>
                                            {showPositionOptions && (
                                                <motion.div
                                                    initial={{ opacity: 0, scale: 0.95 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    exit={{ opacity: 0, scale: 0.95 }}
                                                    className="absolute z-50 mt-2 max-h-60 w-full overflow-y-auto rounded-xl border bg-white shadow-2xl border-slate-100 p-1"
                                                >
                                                    {positionOptions?.map((option: any) => (
                                                        <div
                                                            key={option.value}
                                                            className="relative flex w-full cursor-pointer select-none items-center rounded-lg py-2.5 pl-9 pr-3 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-primary transition-colors"
                                                            onClick={() => {
                                                                setSelectedPosition(option.value);
                                                                form.setFieldValue("position", option.value);
                                                                setShowPositionOptions(false);
                                                            }}
                                                        >
                                                            {selectedPosition === option.value && <Check className="absolute left-3 h-4 w-4 text-primary" />}
                                                            {option.label}
                                                        </div>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </Form.Item>

                                <Form.Item
                                    name="seniority"
                                    className="md:col-span-12 lg:col-span-4"
                                    label={<FormLabel label="Seniority Level" required showAsterisk={false} />}
                                    rules={[{ required: true, message: "Required" }]}
                                >
                                    <div className="relative" onClick={e => e.stopPropagation()}>
                                        <div
                                            onClick={e => { e.stopPropagation(); setShowSeniorityOptions(!showSeniorityOptions); }}
                                            className="flex h-12 w-full cursor-pointer items-center justify-between rounded-xl border border-input bg-background px-4 py-2 text-sm transition-all hover:border-primary/50"
                                        >
                                            <div className="flex items-center gap-2">
                                                <Award className="h-4 w-4 text-muted-foreground" />
                                                <span className="font-medium text-slate-700">{seniorityOptions?.find((opt: any) => opt.value === selectedSeniority)?.label ?? "Select Level"}</span>
                                            </div>
                                            <ChevronDown size={16} className="text-muted-foreground" />
                                        </div>
                                        <AnimatePresence>
                                            {showSeniorityOptions && (
                                                <motion.div
                                                    initial={{ opacity: 0, scale: 0.95 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    exit={{ opacity: 0, scale: 0.95 }}
                                                    className="absolute z-50 mt-2 max-h-60 w-full overflow-y-auto rounded-xl border bg-white shadow-2xl border-slate-100 p-1"
                                                >
                                                    {seniorityOptions?.map((option: any) => (
                                                        <div
                                                            key={option.value}
                                                            className="relative flex w-full cursor-pointer select-none items-center rounded-lg py-2.5 pl-9 pr-3 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-primary transition-colors"
                                                            onClick={() => {
                                                                setSelectedSeniority(option.value);
                                                                form.setFieldValue("seniority", option.value);
                                                                setShowSeniorityOptions(false);
                                                            }}
                                                        >
                                                            {selectedSeniority === option.value && <Check className="absolute left-3 h-4 w-4 text-primary" />}
                                                            {option.label}
                                                        </div>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </Form.Item>

                                <Form.Item
                                    name="skill"
                                    className="md:col-span-12 lg:col-span-4"
                                    label={<FormLabel label="Gov Skills" required showAsterisk={false} />}
                                    rules={[{ required: true, message: "Required" }]}
                                >
                                    <div className="relative" onClick={e => e.stopPropagation()}>
                                        <div
                                            onClick={e => { e.stopPropagation(); setShowSkillsOptions(!showSkillsOptions); }}
                                            className="flex min-h-[3rem] w-full cursor-pointer items-center justify-between rounded-xl border border-input bg-background px-4 py-2 text-sm transition-all hover:border-primary/50"
                                        >
                                            <div className="flex flex-wrap gap-1.5 py-1">
                                                {selectedSkills.length > 0 ? (
                                                    selectedSkills.map(skillId => {
                                                        const skill = skillsOptions?.find((opt: any) => opt.value === skillId);
                                                        return (
                                                            <span key={skillId} className="inline-flex items-center gap-1 rounded-lg bg-primary/10 px-2 py-1 text-xs font-bold text-primary border border-primary/20">
                                                                {skill?.label || skillId}
                                                                <span
                                                                    className="hover:text-destructive transition-colors cursor-pointer"
                                                                    onClick={(e) => { e.stopPropagation(); toggleSkill(skillId); }}
                                                                >×</span>
                                                            </span>
                                                        );
                                                    })
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <Shield className="h-4 w-4 text-muted-foreground" />
                                                        <span className="text-muted-foreground">Select Skills</span>
                                                    </div>
                                                )}
                                            </div>
                                            <ChevronDown size={16} className="text-muted-foreground shrink-0 ml-2" />
                                        </div>
                                        <AnimatePresence>
                                            {showSkillsOptions && (
                                                <motion.div
                                                    initial={{ opacity: 0, scale: 0.95 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    exit={{ opacity: 0, scale: 0.95 }}
                                                    className="absolute z-50 mt-2 max-h-60 w-full overflow-y-auto rounded-xl border bg-white shadow-2xl border-slate-100 p-1"
                                                >
                                                    {skillsOptions?.map((option: any) => (
                                                        <div
                                                            key={option.value}
                                                            className="relative flex w-full cursor-pointer select-none items-center rounded-lg py-2.5 pl-9 pr-3 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-primary transition-colors"
                                                            onClick={() => toggleSkill(option.value)}
                                                        >
                                                            {selectedSkills.includes(option.value) && <Check className="absolute left-3 h-4 w-4 text-primary" />}
                                                            {option.label}
                                                        </div>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </Form.Item>

                                <Form.Item
                                    name="skillintrests"
                                    className="md:col-span-12 lg:col-span-4"
                                    label={<FormLabel label="Skill Interests" required showAsterisk={false} />}
                                    rules={[{ required: true, message: "Required" }]}
                                >
                                    <div className="relative" onClick={e => e.stopPropagation()}>
                                        <div
                                            onClick={e => { e.stopPropagation(); setShowSkillInterestsOptions(!showSkillInterestsOptions); }}
                                            className="flex min-h-[3rem] w-full cursor-pointer items-center justify-between rounded-xl border border-input bg-background px-4 py-2 text-sm transition-all hover:border-primary/50"
                                        >
                                            <div className="flex flex-wrap gap-1.5 py-1">
                                                {selectedSkillInterests.length > 0 ? (
                                                    selectedSkillInterests.map(id => {
                                                        const interest = skillInterestsOptions.find(opt => opt.value === id);
                                                        return (
                                                            <span key={id} className="inline-flex items-center gap-1 rounded-lg bg-primary/5 px-2 py-1 text-xs font-bold text-primary border border-primary/20">
                                                                {interest?.label || id}
                                                                <span
                                                                    className="hover:text-destructive transition-colors cursor-pointer pl-1 border-l border-primary/20 ml-1"
                                                                    onClick={(e) => { e.stopPropagation(); toggleInterest(id); }}
                                                                >×</span>
                                                            </span>
                                                        );
                                                    })
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <Heart className="h-4 w-4 text-muted-foreground" />
                                                        <span className="text-muted-foreground">Select Interests</span>
                                                    </div>
                                                )}
                                            </div>
                                            <ChevronDown size={16} className="text-muted-foreground shrink-0 ml-2" />
                                        </div>
                                        <AnimatePresence>
                                            {showSkillInterestsOptions && (
                                                <motion.div
                                                    initial={{ opacity: 0, scale: 0.95 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    exit={{ opacity: 0, scale: 0.95 }}
                                                    className="absolute z-50 mt-2 max-h-60 w-full overflow-y-auto rounded-xl border bg-white shadow-2xl border-slate-100 p-1"
                                                >
                                                    {skillInterestsOptions.map(option => (
                                                        <div
                                                            key={option.value}
                                                            className="relative flex w-full cursor-pointer select-none items-center rounded-lg py-2.5 pl-9 pr-3 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-primary transition-colors"
                                                            onClick={() => toggleInterest(option.value)}
                                                        >
                                                            {selectedSkillInterests.includes(option.value) && <Check className="absolute left-3 h-4 w-4 text-primary" />}
                                                            {option.label}
                                                        </div>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </Form.Item>
                            </motion.div>

                            {/* Contact & Communications Section */}
                            <SectionHeader
                                icon={Mail}
                                title="Contact & Communications"
                                description="How your colleagues and the organization can reach you"
                            />

                            <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                                <Form.Item
                                    name="phonenumber"
                                    label={<FormLabel label="Phone Number" required showAsterisk={false} />}
                                    rules={[{ required: true, message: "Required" }, { pattern: /^\d{10}$/, message: "Must be 10 digits" }]}
                                >
                                    <FormInput icon={Phone} placeholder="10-digit phone" inputMode="numeric" />
                                </Form.Item>

                                <Form.Item
                                    name="email"
                                    label={<FormLabel label="Login Email" required showAsterisk={false} />}
                                    rules={[{ required: true, message: "Email is required" }, { type: "email", message: "Please enter a valid email address" }]}
                                >
                                    <FormInput icon={Mail} placeholder="Email" className="bg-slate-50 border-slate-200" readOnly />
                                </Form.Item>

                                <Form.Item
                                    name="workemail"
                                    label={<FormLabel label="Work Email (Optional)" />}
                                    rules={[{ type: "email", message: "Please enter a valid email address" }]}
                                >
                                    <FormInput icon={Mail} placeholder="Work email" />
                                </Form.Item>

                                <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <Form.Item
                                        name="address"
                                        label={<FormLabel label="Primary Dept. Address" required showAsterisk={false} />}
                                        rules={[{ required: true, message: "Required" }]}
                                    >
                                        <div className="relative">
                                            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                                            <GoogleAddressInput
                                                value={form.getFieldValue("address")}
                                                placeholder="Enter primary address"
                                                className="w-full pl-11 h-12 rounded-xl"
                                                onChange={handleAddressChange}
                                            />
                                        </div>
                                    </Form.Item>
                                    <Form.Item
                                        name="cityaddress"
                                        label={<FormLabel label="Secondary/City Address" required showAsterisk={false} />}
                                        rules={[{ required: true, message: "Required" }]}
                                    >
                                        <div className="relative">
                                            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                                            <GoogleAddressInput
                                                value={form.getFieldValue("cityaddress")}
                                                placeholder="Enter city address"
                                                className="w-full pl-11 h-12 rounded-xl"
                                                onChange={handleCityAddressChange}
                                            />
                                        </div>
                                    </Form.Item>
                                </div>
                            </motion.div>

                            {/* Additional Information Section */}
                            <SectionHeader
                                icon={Info}
                                title="Additional Information"
                                description="Supplementary details to round out your profile"
                            />

                            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                                <Form.Item name="consultation" label={<FormLabel label="Peer Consult" showAsterisk={false} />}>
                                    <div className="relative" onClick={e => e.stopPropagation()}>
                                        <div
                                            onClick={e => { e.stopPropagation(); setShowConsultationOptions(!showConsultationOptions); }}
                                            className="flex h-12 w-full cursor-pointer items-center justify-between rounded-xl border border-input bg-background px-4 py-2 text-sm transition-all hover:border-primary/50"
                                        >
                                            <div className="flex items-center gap-2">
                                                <Handshake className="h-4 w-4 text-muted-foreground" />
                                                <span className="font-medium text-slate-700">{selectedConsultation || "Select Option"}</span>
                                            </div>
                                            <ChevronDown size={16} className="text-muted-foreground" />
                                        </div>
                                        <AnimatePresence>
                                            {showConsultationOptions && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -10 }}
                                                    className="absolute z-50 mt-2 w-full overflow-y-auto rounded-xl border bg-white shadow-2xl border-slate-100 p-1"
                                                >
                                                    {["Yes", "No"].map(option => (
                                                        <div key={option} className="relative flex w-full cursor-pointer select-none items-center rounded-lg py-2.5 pl-9 pr-3 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-primary transition-colors"
                                                            onClick={() => { setSelectedConsultation(option); form.setFieldValue("consultation", option); setShowConsultationOptions(false); }}
                                                        >
                                                            {selectedConsultation === option && <Check className="absolute left-3 h-4 w-4 text-primary" />}
                                                            {option}
                                                        </div>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </Form.Item>

                                <Form.Item name="contract" label={<FormLabel label="Contract" showAsterisk={false} />}>
                                    <div className="relative" onClick={e => e.stopPropagation()}>
                                        <div
                                            onClick={e => { e.stopPropagation(); setShowContractOptions(!showContractOptions); }}
                                            className="flex h-12 w-full cursor-pointer items-center justify-between rounded-xl border border-input bg-background px-4 py-2 text-sm transition-all hover:border-primary/50"
                                        >
                                            <div className="flex items-center gap-2">
                                                <FileText className="h-4 w-4 text-muted-foreground" />
                                                <span className="font-medium text-slate-700">{selectedContract || "Select Option"}</span>
                                            </div>
                                            <ChevronDown size={16} className="text-muted-foreground" />
                                        </div>
                                        <AnimatePresence>
                                            {showContractOptions && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -10 }}
                                                    className="absolute z-50 mt-2 w-full overflow-y-auto rounded-xl border bg-white shadow-2xl border-slate-100 p-1"
                                                >
                                                    {["Yes", "No"].map(option => (
                                                        <div key={option} className="relative flex w-full cursor-pointer select-none items-center rounded-lg py-2.5 pl-9 pr-3 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-primary transition-colors"
                                                            onClick={() => { setSelectedContract(option); form.setFieldValue("contract", option); setShowContractOptions(false); }}
                                                        >
                                                            {selectedContract === option && <Check className="absolute left-3 h-4 w-4 text-primary" />}
                                                            {option}
                                                        </div>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </Form.Item>

                                <Form.Item name="marketplace" label={<FormLabel label="Marketplace" showAsterisk={false} />}>
                                    <div className="relative" onClick={e => e.stopPropagation()}>
                                        <div
                                            onClick={e => { e.stopPropagation(); setShowMarketplaceOptions(!showMarketplaceOptions); }}
                                            className="flex h-12 w-full cursor-pointer items-center justify-between rounded-xl border border-input bg-background px-4 py-2 text-sm transition-all hover:border-primary/50"
                                        >
                                            <div className="flex items-center gap-2">
                                                <Store className="h-4 w-4 text-muted-foreground" />
                                                <span className="font-medium text-slate-700">{selectedMarketplace || "Select Option"}</span>
                                            </div>
                                            <ChevronDown size={16} className="text-muted-foreground" />
                                        </div>
                                        <AnimatePresence>
                                            {showMarketplaceOptions && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -10 }}
                                                    className="absolute z-50 mt-2 w-full overflow-y-auto rounded-xl border bg-white shadow-2xl border-slate-100 p-1"
                                                >
                                                    {["Yes", "No"].map(option => (
                                                        <div key={option} className="relative flex w-full cursor-pointer select-none items-center rounded-lg py-2.5 pl-9 pr-3 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-primary transition-colors"
                                                            onClick={() => { setSelectedMarketplace(option); form.setFieldValue("marketplace", option); setShowMarketplaceOptions(false); }}
                                                        >
                                                            {selectedMarketplace === option && <Check className="absolute left-3 h-4 w-4 text-primary" />}
                                                            {option}
                                                        </div>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </Form.Item>

                                <Form.Item name="opportunities" label={<FormLabel label="Retire Ops" showAsterisk={false} />}>
                                    <div className="relative" onClick={e => e.stopPropagation()}>
                                        <div
                                            onClick={e => { e.stopPropagation(); setShowOpportunitiesOptions(!showOpportunitiesOptions); }}
                                            className="flex h-12 w-full cursor-pointer items-center justify-between rounded-xl border border-input bg-background px-4 py-2 text-sm transition-all hover:border-primary/50"
                                        >
                                            <div className="flex items-center gap-2">
                                                <Activity className="h-4 w-4 text-muted-foreground" />
                                                <span className="font-medium text-slate-700">{selectedOpportunities || "Select Option"}</span>
                                            </div>
                                            <ChevronDown size={16} className="text-muted-foreground" />
                                        </div>
                                        <AnimatePresence>
                                            {showOpportunitiesOptions && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -10 }}
                                                    className="absolute z-50 mt-2 w-full overflow-y-auto rounded-xl border bg-white shadow-2xl border-slate-100 p-1"
                                                >
                                                    {["Advisory Role", "Contract Work", "Peer Mentoring"].map(option => (
                                                        <div key={option} className="relative flex w-full cursor-pointer select-none items-center rounded-lg py-2.5 pl-9 pr-3 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-primary transition-colors"
                                                            onClick={() => { setSelectedOpportunities(option); form.setFieldValue("opportunities", option); setShowOpportunitiesOptions(false); }}
                                                        >
                                                            {selectedOpportunities === option && <Check className="absolute left-3 h-4 w-4 text-primary" />}
                                                            {option}
                                                        </div>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </Form.Item>
                                <Form.Item name="emergencyroles" className="sm:col-span-2 lg:col-span-2" label={<FormLabel label="Emergency Roles" />}>
                                    <FormInput icon={Shield} placeholder="List emergency roles if any" />
                                </Form.Item>

                                <Form.Item name="certificate1" className="sm:col-span-2 lg:col-span-2" label={<FormLabel label="Medals / Recognition" />}>
                                    <FormInput icon={Award} placeholder="Enter medals or honors" />
                                </Form.Item>


                                <Form.Item name="supervisor" className="sm:col-span-2 lg:col-span-2" label={<FormLabel label="Supervisor" />}>
                                    <FormInput icon={Users} placeholder="Enter supervisor name" />
                                </Form.Item>
                            </motion.div>
                            <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                                <Form.Item name="responsibility" label={<FormLabel label="Core Responsibilities" />}>
                                    <FormTextArea icon={Activity} placeholder="Outline your primary job responsibilities..." />
                                </Form.Item>
                                <Form.Item name="description" label={<FormLabel label="Professional Bio / Description" />}>
                                    <FormTextArea icon={FileText} placeholder="Write a brief professional bio or description..." />
                                </Form.Item>
                            </motion.div>

                            <motion.div variants={itemVariants} className="flex justify-end gap-4 mt-12 pt-8">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="h-12 px-8 rounded-xl font-bold text-slate-600 border-slate-200 hover:bg-slate-50"
                                    onClick={handleBackToLogin}
                                >
                                    Cancel
                                </Button>                                 <Button
                                    type="submit"
                                    className="h-12 w-full sm:w-auto px-12 rounded-xl font-bold bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all hover:translate-y-[-2px]"
                                    disabled={isLoader}
                                >
                                    {isLoader ? (
                                        <div className="flex items-center gap-2">
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Saving...
                                        </div>
                                    ) : (
                                        "Save"
                                    )}
                                </Button>
                            </motion.div>

                        </Form>
                    </div>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default MemberRegistrationPage;
